import React, { useEffect, useState } from 'react'
import { Button } from '../../components/ui/button'
import { getLocalApiUrl, authFetch } from '../../config/api'

/**
 * Assign (internal) or send (patron). Optional presetSubject skips patron search
 * when launching from a record (ECDD / profile).
 */
export default function FormAssignModal({ form, onClose, onDone, presetSubject = null }) {
  const facing = form.facing
  const [users, setUsers] = useState([])
  const [roles, setRoles] = useState([])
  const [patrons, setPatrons] = useState([])
  const [q, setQ] = useState('')
  const [subjectId, setSubjectId] = useState(presetSubject?.subjectId || '')
  const [subjectLabel, setSubjectLabel] = useState(presetSubject?.subjectLabel || '')
  const [assigneeUserId, setAssigneeUserId] = useState('')
  const [assigneeRoleId, setAssigneeRoleId] = useState('')
  const [emailTo, setEmailTo] = useState(presetSubject?.emailTo || '')
  const [smsTo, setSmsTo] = useState(presetSubject?.smsTo || '')
  const [channels, setChannels] = useState('email')
  const [tatDays, setTatDays] = useState(facing === 'internal' ? 7 : 5)
  const [createGovernanceTask, setCreateGovernanceTask] = useState(true)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [formUrl, setFormUrl] = useState('')

  useEffect(() => {
    authFetch(getLocalApiUrl('/api/forms/catalogs/users')).then((r) => r.json()).then((j) => setUsers(j.data || [])).catch(() => {})
    authFetch(getLocalApiUrl('/api/forms/catalogs/roles')).then((r) => r.json()).then((j) => setRoles(j.data || [])).catch(() => {})
  }, [])

  const searchPatrons = async () => {
    const res = await authFetch(getLocalApiUrl(`/api/forms/catalogs/patrons?q=${encodeURIComponent(q)}`))
    const j = await res.json()
    setPatrons(j.data || [])
  }

  const selectPatron = (p) => {
    setSubjectId(p.value)
    setSubjectLabel(p.label)
    setEmailTo(p.email || '')
    setSmsTo(p.phone || '')
  }

  const submit = async () => {
    setBusy(true)
    setError('')
    try {
      const body = {
        formId: form.id,
        facing,
        subjectType: presetSubject?.subjectType || (facing === 'patron' ? 'patron' : 'none'),
        subjectId: subjectId || null,
        subjectLabel: subjectLabel || null,
        assigneeUserId: assigneeUserId || null,
        assigneeRoleId: assigneeUserId ? null : assigneeRoleId || null,
        emailTo: emailTo || null,
        smsTo: smsTo || null,
        channels: facing === 'patron' ? channels : null,
        tatDays: Number(tatDays) || null,
        createGovernanceTask: facing === 'internal' ? createGovernanceTask : undefined,
      }
      const res = await authFetch(getLocalApiUrl('/api/forms/assignments'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })
      const j = await res.json()
      if (!res.ok || !j.success) throw new Error(j.error || (facing === 'patron' ? 'Send failed' : 'Assign failed'))
      if (j.formUrl) setFormUrl(j.formUrl)
      else onDone(j.assignment || j)
    } catch (e) {
      setError(e.message)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="fixed inset-0 z-[110] bg-black/30 flex items-center justify-center p-4">
      <div className="w-full max-w-lg rounded-xl bg-white p-5 shadow-lg">
        <h2 className="text-lg font-semibold mb-1">{facing === 'patron' ? 'Send' : 'Assign'} {form.name}</h2>
        <p className="text-sm text-slate-500 mb-4">
          {facing === 'patron'
            ? (presetSubject?.subjectLabel ? `Send to ${presetSubject.subjectLabel}.` : 'Select a patron. Email / SMS defaults to their profile contact.')
            : (presetSubject?.subjectLabel ? `Staff fill for ${presetSubject.subjectLabel}.` : 'Assign to an application user or a role.')}
        </p>
        {error ? <div className="mb-3 text-sm text-red-600">{error}</div> : null}
        {facing === 'patron' ? (
          <div className="space-y-3">
            {presetSubject?.subjectId ? (
              <div className="rounded-lg border border-teal-200 bg-teal-50 px-3 py-2 text-sm font-medium text-teal-900">{subjectLabel}</div>
            ) : (
              <>
                <div className="flex gap-2">
                  <input
                    className="flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm"
                    placeholder="Search patron name or membership #"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault()
                        searchPatrons()
                      }
                    }}
                  />
                  <Button variant="outline" onClick={searchPatrons}>Search</Button>
                </div>
                <div className="max-h-40 overflow-auto border border-slate-200 rounded-lg">
                  {patrons.map((p) => (
                    <button
                      key={p.value}
                      type="button"
                      className={`w-full text-left px-3 py-2 text-sm ${subjectId === p.value ? 'bg-teal-50' : 'hover:bg-slate-50'}`}
                      onClick={() => selectPatron(p)}
                    >
                      {p.label}
                    </button>
                  ))}
                </div>
                {subjectLabel ? <div className="text-sm font-medium text-teal-800">Selected: {subjectLabel}</div> : null}
              </>
            )}
            <label className="block text-xs font-semibold">Email<input className="mt-1 w-full rounded-lg border px-3 py-2 text-sm" value={emailTo} onChange={(e) => setEmailTo(e.target.value)} /></label>
            <label className="block text-xs font-semibold">SMS<input className="mt-1 w-full rounded-lg border px-3 py-2 text-sm" value={smsTo} onChange={(e) => setSmsTo(e.target.value)} /></label>
            <div className="grid grid-cols-2 gap-3">
              <label className="block text-xs font-semibold">Channel
                <select className="mt-1 w-full rounded-lg border px-3 py-2 text-sm" value={channels} onChange={(e) => setChannels(e.target.value)}>
                  <option value="email">Email</option>
                  <option value="sms">SMS</option>
                  <option value="both">Both</option>
                </select>
              </label>
              <label className="block text-xs font-semibold">TAT days
                <input type="number" className="mt-1 w-full rounded-lg border px-3 py-2 text-sm" value={tatDays} onChange={(e) => setTatDays(e.target.value)} />
              </label>
            </div>
            <label className="block text-xs font-semibold">Review owner (user)
              <select className="mt-1 w-full rounded-lg border px-3 py-2 text-sm" value={assigneeUserId} onChange={(e) => { setAssigneeUserId(e.target.value); if (e.target.value) setAssigneeRoleId('') }}>
                <option value="">—</option>
                {users.map((u) => <option key={u.value} value={u.value}>{u.label}</option>)}
              </select>
            </label>
            <label className="block text-xs font-semibold">Or review role
              <select className="mt-1 w-full rounded-lg border px-3 py-2 text-sm" value={assigneeRoleId} onChange={(e) => { setAssigneeRoleId(e.target.value); if (e.target.value) setAssigneeUserId('') }}>
                <option value="">—</option>
                {roles.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
            </label>
          </div>
        ) : (
          <div className="space-y-3">
            {presetSubject?.subjectLabel ? (
              <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-800">Subject: {presetSubject.subjectLabel}</div>
            ) : null}
            <label className="block text-xs font-semibold">Assign to user
              <select className="mt-1 w-full rounded-lg border px-3 py-2 text-sm" value={assigneeUserId} onChange={(e) => { setAssigneeUserId(e.target.value); if (e.target.value) setAssigneeRoleId('') }}>
                <option value="">—</option>
                {users.map((u) => <option key={u.value} value={u.value}>{u.label}</option>)}
              </select>
            </label>
            <label className="block text-xs font-semibold">Or assign to role
              <select className="mt-1 w-full rounded-lg border px-3 py-2 text-sm" value={assigneeRoleId} onChange={(e) => { setAssigneeRoleId(e.target.value); if (e.target.value) setAssigneeUserId('') }}>
                <option value="">—</option>
                {roles.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
            </label>
            <label className="block text-xs font-semibold">Due in (days)
              <input type="number" min={1} className="mt-1 w-full rounded-lg border px-3 py-2 text-sm" value={tatDays} onChange={(e) => setTatDays(e.target.value)} />
            </label>
            <label className="flex items-start gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                className="mt-1"
                checked={createGovernanceTask}
                onChange={(e) => setCreateGovernanceTask(e.target.checked)}
              />
              <span>
                Create a <strong>Governance task</strong> for the assignee (recommended). They can open it from Governance Safe → Tasks.
              </span>
            </label>
          </div>
        )}
        {formUrl ? (
          <div className="mt-4 rounded-lg bg-slate-50 border border-slate-200 p-3 text-sm">
            <div className="font-semibold mb-1">Sent. Share this link if needed:</div>
            <div className="break-all text-teal-800">{formUrl}</div>
            <Button className="mt-3" onClick={() => onDone()}>Done</Button>
          </div>
        ) : (
          <div className="flex justify-end gap-2 mt-5">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={submit} disabled={busy}>{busy ? 'Working…' : facing === 'patron' ? 'Send' : 'Assign'}</Button>
          </div>
        )}
      </div>
    </div>
  )
}
